/**
 * The files of a drag and drop (UPL-01), with dropped folders read recursively. The entries are
 * taken while the drop event runs, since the browser empties its data transfer afterwards.
 */
export function droppedFiles(transfer: DataTransfer): Promise<File[]> {
  const entries: FileSystemEntry[] = []
  const files: File[] = []
  for (const item of [...transfer.items]) {
    if (item.kind !== 'file') continue
    const entry = item.webkitGetAsEntry?.()
    if (entry) entries.push(entry)
    else {
      const file = item.getAsFile()
      if (file) files.push(file)
    }
  }
  if (entries.length === 0) return Promise.resolve(files.length ? files : [...transfer.files])
  return collect(entries).then((nested) => [...files, ...nested])
}

async function collect(entries: FileSystemEntry[]): Promise<File[]> {
  const files: File[] = []
  const pending = [...entries]
  for (let entry = pending.pop(); entry; entry = pending.pop()) {
    if (isFile(entry)) {
      const file = await new Promise<File>((resolve, reject) => entry.file(resolve, reject))
      // Folder paths distinguish equal file names and make resume fingerprints stable.
      Object.defineProperty(file, 'webkitRelativePath', { value: entry.fullPath.replace(/^\//, ''), configurable: true })
      files.push(file)
    }
    else if (isDirectory(entry)) pending.push(...(await readAll(entry.createReader())))
  }
  return files
}

/** A directory reader answers in batches (about 100 entries each) until an empty one. */
async function readAll(reader: FileSystemDirectoryReader): Promise<FileSystemEntry[]> {
  const all: FileSystemEntry[] = []
  for (;;) {
    const batch = await new Promise<FileSystemEntry[]>((resolve, reject) => reader.readEntries(resolve, reject))
    if (batch.length === 0) return all
    all.push(...batch)
  }
}

const isFile = (entry: FileSystemEntry): entry is FileSystemFileEntry => entry.isFile
const isDirectory = (entry: FileSystemEntry): entry is FileSystemDirectoryEntry => entry.isDirectory
